import axios from "axios";

/**
 * Central axios client for AFRICTAL V1 backend — V3.10 secured.
 *
 * SECURITY MODEL (V3.10)
 * ─────────────────────────────────────────────────────────────────────
 *   • Auth token is stored in an **httpOnly cookie** (`access_token`) —
 *     unreachable from JavaScript, immune to XSS token theft.
 *   • CSRF protection via **double-submit cookie** pattern :
 *     - Backend sets a `csrf_token` cookie (NOT httpOnly, JS-readable).
 *     - This client copies it into the `X-CSRF-Token` header on every
 *       mutating request (POST/PUT/PATCH/DELETE).
 *     - The backend CSRFMiddleware validates the two match.
 *   • Bearer token is NO LONGER stored in localStorage — this removes
 *     the XSS attack surface entirely.
 *
 * Legacy `getAccessToken()` / `setAccessToken()` are kept as **no-ops**
 * for backward compatibility with older code paths that referenced them.
 */

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const API_ROOT = `${BACKEND}/api`;

// ─── Legacy no-ops (kept to avoid breaking imports) ─────────────────
export function setAccessToken(_token) {
  // No-op : auth is now in httpOnly cookies only. Kept for API compat.
}

export function getAccessToken() {
  // No-op : the JWT is inaccessible from JS by design (httpOnly cookie).
  return null;
}

// ─── CSRF helper ─────────────────────────────────────────────────────
export function getCsrfToken() {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

// ─── Axios instance ──────────────────────────────────────────────────
export const api = axios.create({
  baseURL: API_ROOT,
  withCredentials: true, // always send httpOnly auth cookie
  headers: { "Content-Type": "application/json" },
});

// Attach CSRF header on every mutating request (double-submit pattern).
api.interceptors.request.use((config) => {
  const method = (config.method || "get").toUpperCase();
  if (["POST", "PUT", "PATCH", "DELETE"].includes(method)) {
    const csrf = getCsrfToken();
    if (csrf) {
      config.headers = { ...(config.headers || {}), "X-CSRF-Token": csrf };
    }
  }
  return config;
});

let refreshInFlight = null;

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    if (status === 401 && !original.__retried && !original.url?.includes("/auth/")) {
      original.__retried = true;
      try {
        refreshInFlight =
          refreshInFlight ||
          api.post("/auth/refresh").finally(() => {
            refreshInFlight = null;
          });
        await refreshInFlight;
        return api(original);
      } catch (_) {
        // Refresh failed — user must re-login; cookies already cleared server-side.
      }
    }
    return Promise.reject(error);
  }
);

/** Formats FastAPI error responses (which may be arrays or objects) into a string. */
export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Une erreur est survenue.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => (d && typeof d.msg === "string" ? d.msg : JSON.stringify(d)))
      .filter(Boolean)
      .join(" · ");
  }
  if (detail && typeof detail.message === "string") return detail.message;
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
